<template>
  <div class="erp-page page">
    <div class="erp-page-header page-hero">
      <div>
        <h1 class="erp-page-title">Configuración AFIP</h1>
        <p>Empresa, certificados, ticket de acceso y conexión WSFE para facturación electrónica.</p>
      </div>
      <v-btn color="primary" :loading="loading" @click="fetchAll">Recargar</v-btn>
    </div>

    <v-alert v-if="error" type="error" dense outlined dismissible @input="error = ''">{{ error }}</v-alert>
    <v-alert v-if="readiness" :type="readiness.ready ? 'success' : 'warning'" dense outlined>
      {{ readiness.ready ? 'AFIP listo para operar.' : 'AFIP aún no está listo: completá los controles pendientes.' }}
      <span class="ml-2">Ambiente: {{ readiness.environment }}.</span>
    </v-alert>
    <v-alert v-if="success" type="success" dense outlined dismissible @input="success = ''">{{ success }}</v-alert>

    <v-card outlined class="page-card mb-4">
      <v-card-title class="subtitle-1">Contingencia CAEA</v-card-title>
      <v-card-text>
        <v-alert v-if="caeaReadiness" :type="caeaReadiness.enabled ? 'warning' : 'info'" dense outlined class="mb-3">
          <template v-if="caeaReadiness.enabled">CAEA está habilitado: se usará únicamente mediante el flujo de contingencia y un punto de venta exclusivo.</template>
          <template v-else>CAEA está preparado pero deshabilitado. La facturación habitual continúa solicitando CAE en línea.</template>
        </v-alert>
        <div v-if="caeaReadiness" class="text-caption">
          Punto de venta exclusivo: {{ caeaReadiness.contingencyPointOfSale ? `${caeaReadiness.contingencyPointOfSale.number} — ${caeaReadiness.contingencyPointOfSale.name}` : 'pendiente de configurar' }}.
          No habilites CAEA hasta contar con ese punto de venta dado de alta en ARCA.
        </div>
      </v-card-text>
    </v-card>

    <v-row dense>
      <v-col cols="12" md="5">
        <v-card outlined class="page-card mb-4">
          <v-card-title class="subtitle-1">Generar CSR</v-card-title>
          <v-card-text>
            <v-text-field v-model="csrForm.alias" label="Alias" outlined dense />
            <v-text-field v-model="csrForm.companyCuit" label="CUIT empresa representada" outlined dense />
            <v-text-field v-model="csrForm.certificateOwnerCuit" label="CUIT persona física titular del certificado" outlined dense hint="En homologación debe coincidir con la persona que ingresa a WSASS." persistent-hint />
            <v-select v-model="csrForm.environment" :items="environmentOptions" label="Ambiente" outlined dense />
            <v-btn color="primary" block :loading="savingCsr" @click="generateCsr">Generar CSR</v-btn>
          </v-card-text>
        </v-card>

        <v-card v-if="configs.length" outlined class="page-card mb-4">
          <v-card-title class="subtitle-1">Condición IVA del emisor</v-card-title>
          <v-card-text>
            <p class="text-caption mb-3">Define la matriz de comprobantes: Responsable Inscripto emite A o B según el receptor; Monotributo o Exento emite C.</p>
            <v-select v-model="issuerTaxCondition" :items="issuerTaxConditionOptions" item-text="text" item-value="value" label="Condición IVA" outlined dense />
            <v-btn color="primary" block :loading="savingCompany" @click="saveIssuerTaxCondition">Guardar condición fiscal</v-btn>
          </v-card-text>
        </v-card>

        <v-card outlined class="page-card mb-4">
          <v-card-title class="subtitle-1">Refrescar Ticket de Acceso</v-card-title>
          <v-card-text>
            <v-text-field v-model="connectionForm.companyCuit" label="CUIT empresa" outlined dense />
            <v-select v-model="connectionForm.environment" :items="environmentOptions" label="Ambiente" outlined dense />
            <v-select v-model="connectionForm.wsName" :items="['wsfe']" label="Webservice" outlined dense />
            <v-btn color="success" block :loading="refreshing" @click="refreshConnection">Refrescar TA</v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="7">
        <v-card outlined class="page-card mb-4">
          <v-card-title class="subtitle-1">Empresa</v-card-title>
          <v-card-text>
            <v-alert v-if="!configs.length && !loading" type="info" dense text>No hay configuración de empresa cargada.</v-alert>
            <v-data-table :headers="configHeaders" :items="configs" :loading="loading" dense hide-default-footer />
          </v-card-text>
        </v-card>

        <v-card outlined class="page-card mb-4">
          <v-card-title class="subtitle-1">Conexiones</v-card-title>
          <v-card-text>
            <v-alert v-if="!connections.length && !loading" type="info" dense text>No hay tickets de acceso generados todavía.</v-alert>
            <v-data-table :headers="connHeaders" :items="connections" :loading="loading" dense>
              <template v-slot:item.environment="{ item }">
                <v-chip x-small color="info" dark label>{{ item.environment }}</v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="page-card">
      <v-card-title class="subtitle-1">Certificados</v-card-title>
      <v-card-text>
        <v-alert v-if="!certificates.length && !loading" type="info" dense text>Generá un CSR para crear el primer certificado.</v-alert>
        <v-data-table :headers="certHeaders" :items="certificates" :loading="loading" dense>
          <template v-slot:item.isActive="{ item }">
            <v-chip x-small :color="item.isActive ? 'success' : 'grey'" dark label>{{ item.isActive ? "Activo" : "Inactivo" }}</v-chip>
          </template>
          <template v-slot:item.hasSignedCertificate="{ item }">
            <v-chip x-small :color="item.hasSignedCertificate ? 'success' : 'warning'" dark label>{{ item.hasSignedCertificate ? "Firmado" : "Pendiente" }}</v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn x-small text color="primary" @click="copyCsr(item)">Copiar CSR</v-btn>
            <v-btn x-small text color="success" @click="openUpload(item)">Subir firmado</v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="uploadDialog" max-width="760">
      <v-card>
        <v-card-title>Subir certificado firmado</v-card-title>
        <v-card-text>
          <v-textarea v-model="uploadForm.certificatePem" label="Certificado PEM firmado por AFIP" outlined rows="10" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="uploadDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="uploading" @click="uploadCertificate">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { afipService } from "../services/afip";

export default {
  name: "AfipConfigView",
  data() {
    return {
      certificates: [],
      connections: [],
      configs: [],
      loading: false,
      readiness: null,
      caeaReadiness: null,
      savingCsr: false,
      refreshing: false,
      savingCompany: false,
      issuerTaxCondition: "",
      issuerTaxConditionOptions: [
        { text: "Responsable Inscripto", value: "responsable_inscripto" },
        { text: "Monotributo", value: "monotributo" },
        { text: "Exento", value: "exento" },
      ],
      uploading: false,
      error: "",
      success: "",
      uploadDialog: false,
      selectedCertificate: null,
      environmentOptions: ["homologation", "production"],
      csrForm: { alias: "", companyCuit: "", certificateOwnerCuit: "", environment: "homologation" },
      connectionForm: { companyCuit: "", environment: "homologation", wsName: "wsfe" },
      uploadForm: { certificatePem: "" },
      certHeaders: [
        { text: "Alias", value: "alias" },
        { text: "CUIT", value: "companyCuit" },
        { text: "Ambiente", value: "environment" },
        { text: "Estado", value: "isActive" },
        { text: "Certificado", value: "hasSignedCertificate" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      connHeaders: [
        { text: "CUIT", value: "companyCuit" },
        { text: "WS", value: "afipWs" },
        { text: "Ambiente", value: "environment" },
        { text: "Expira", value: "expirationTime" },
      ],
      configHeaders: [
        { text: "Nombre", value: "companyName" },
        { text: "CUIT", value: "cuit" },
        { text: "Ambiente AFIP", value: "afipEnvironment" },
        { text: "Condición IVA", value: "issuerTaxCondition" },
      ],
    };
  },
  created() {
    this.fetchAll();
  },
  methods: {
    unwrap(response) {
      if (response && response.data && response.data.data !== undefined) return response.data.data;
      return response && response.data ? response.data : [];
    },
    parseError(error) {
      return error?.response?.data?.error || error?.response?.data?.message || error?.message || "No se pudo completar la operación";
    },
    applyDefaultCompany() {
      const company = this.configs[0];
      if (!company) return;
      if (!this.csrForm.companyCuit) this.csrForm.companyCuit = company.cuit || "";
      if (!this.connectionForm.companyCuit) this.connectionForm.companyCuit = company.cuit || "";
      if (!this.issuerTaxCondition) this.issuerTaxCondition = company.issuerTaxCondition || "";
      if (company.afipEnvironment) {
        this.csrForm.environment = company.afipEnvironment;
        this.connectionForm.environment = company.afipEnvironment;
      }
    },
    async fetchAll() {
      this.loading = true;
      this.error = "";
      try {
        const [cRes, cnRes, cfRes, readinessRes, caeaRes] = await Promise.all([
          afipService.listCertificates(), afipService.listConnections(),
          afipService.listCompanyConfigs(), afipService.getReadiness(),
          afipService.getCaeaReadiness(),
        ]);
        this.certificates = this.unwrap(cRes) || [];
        this.connections = this.unwrap(cnRes) || [];
        this.configs = this.unwrap(cfRes) || [];
        this.readiness = this.unwrap(readinessRes) || null;
        this.caeaReadiness = this.unwrap(caeaRes) || null;
        this.applyDefaultCompany();
      } catch (e) { this.error = this.parseError(e); }
      finally { this.loading = false; }
    },
    async saveIssuerTaxCondition() {
      const company = this.configs[0];
      if (!company || !this.issuerTaxCondition) return;
      this.savingCompany = true;
      this.error = "";
      this.success = "";
      try {
        await afipService.updateCompanyConfig(company.id, { issuerTaxCondition: this.issuerTaxCondition });
        this.success = "Condición IVA del emisor actualizada. Las próximas facturas usarán la matriz fiscal correspondiente.";
        await this.fetchAll();
      } catch (e) {
        this.error = this.parseError(e);
      } finally {
        this.savingCompany = false;
      }
    },
    async generateCsr() {
      this.savingCsr = true;
      this.error = "";
      this.success = "";
      try {
        await afipService.createCertificate(this.csrForm);
        this.success = "CSR generado. En WSASS ingresá con la persona física titular, creá el certificado, autorizalo para la empresa y luego pegá aquí el PEM firmado.";
        await this.fetchAll();
      } catch (e) {
        this.error = this.parseError(e);
      } finally {
        this.savingCsr = false;
      }
    },
    async refreshConnection() {
      this.refreshing = true;
      this.error = "";
      this.success = "";
      try {
        await afipService.refreshConnection(this.connectionForm);
        this.success = "Ticket de acceso refrescado.";
        await this.fetchAll();
      } catch (e) {
        this.error = this.parseError(e);
      } finally {
        this.refreshing = false;
      }
    },
    async copyCsr(item) {
      try {
        await navigator.clipboard.writeText(item.csr || "");
        this.success = "CSR copiado al portapapeles.";
      } catch (_e) {
        this.error = "No se pudo copiar el CSR.";
      }
    },
    openUpload(item) {
      this.selectedCertificate = item;
      this.uploadForm = { certificatePem: "" };
      this.uploadDialog = true;
    },
    async uploadCertificate() {
      if (!this.selectedCertificate) return;
      this.uploading = true;
      this.error = "";
      this.success = "";
      try {
        await afipService.uploadCertificate(this.selectedCertificate.id, this.uploadForm);
        this.success = "Certificado firmado guardado.";
        this.uploadDialog = false;
        await this.fetchAll();
      } catch (e) {
        this.error = this.parseError(e);
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped>
.page { padding: 0; }
.page-hero { background: var(--bg-card); border-radius: 8px; padding: 20px 24px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; gap: 12px; box-shadow: var(--shadow-md); border: 1px solid var(--border-subtle); }
.page-hero h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: var(--text-primary); }
.page-hero p { margin: 0; color: var(--text-secondary); font-size: 14px; line-height: 1.5; }
.page-card { background: var(--bg-card); border-radius: 8px; padding: 8px 8px 4px; box-shadow: var(--shadow-md); border: 1px solid var(--border-subtle); }
</style>
